/**
 * Azure Platform - One-Click Connect
 */
import axios from 'axios'
import { AlertCircle, Cloud, Database, HardDrive, Loader2, Plus, RefreshCw, Server, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface AzureAccount {
  id: number
  subscription_id: string
  tenant_id: string
  label: string
  resource_groups: number
  ai_services: number
  storage_accounts: number
  functions_count: number
  status: string
  connected_at: string
}

interface ConnectForm {
  client_id: string
  client_secret: string
  tenant_id: string
  subscription_id: string
  label: string
}

export default function AzurePage() {
  const [accounts, setAccounts] = useState<AzureAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ConnectForm>({ client_id: '', client_secret: '', tenant_id: '', subscription_id: '', label: '' })

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get<{ accounts: AzureAccount[] }>(`${API_URL}/api/aicloud/azure/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(resp.data.accounts ?? [])
    } catch {
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAccounts() }, [])

  const handleConnect = async () => {
    if (!form.client_id || !form.client_secret || !form.tenant_id || !form.subscription_id) {
      setError('All fields except label are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/azure/connect`, form, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setShowAdd(false)
      setForm({ client_id: '', client_secret: '', tenant_id: '', subscription_id: '', label: '' })
      void fetchAccounts()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const handleSync = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/azure/accounts/${id}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchAccounts()
    } catch {
      setError('Sync failed')
    }
  }

  const handleDisconnect = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/azure/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch {
      setError('Failed to disconnect')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800 }}>Microsoft Azure</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Connect your Azure subscriptions for AI services and cloud logic.</p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#0078d4', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
            <Plus size={16} /> Add Azure Account
          </button>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#ef4444', fontSize: '13px', marginBottom: '20px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {showAdd && (
        <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Client ID</label>
              <input type="text" placeholder="Application ID" value={form.client_id}
                onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Client Secret</label>
              <input type="password" placeholder="••••••••••••••••" value={form.client_secret}
                onChange={e => setForm(p => ({ ...p, client_secret: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Tenant ID</label>
              <input type="text" placeholder="Directory ID" value={form.tenant_id}
                onChange={e => setForm(p => ({ ...p, tenant_id: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Subscription ID</label>
              <input type="text" placeholder="Azure Subscription ID" value={form.subscription_id}
                onChange={e => setForm(p => ({ ...p, subscription_id: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Account Label</label>
            <input type="text" placeholder="e.g. Enterprise Azure" value={form.label}
              onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
              style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => void handleConnect()} disabled={saving}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#0078d4', color: 'white', border: 'none', borderRadius: '10px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 700 }}>
              {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={16} />}
              {saving ? 'Connecting Azure...' : 'Connect Subscription'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '12px 24px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#0078d4', margin: '0 auto' }} />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', background: 'var(--card-bg)', border: '2px dashed var(--border-color)', borderRadius: '20px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(0,120,212,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Cloud size={32} color="#0078d4" />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>No Azure Connections</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '300px', margin: '0 auto 20px' }}>Connect your Azure AD Service Principal to manage cloud services.</p>
          <button onClick={() => setShowAdd(true)} style={{ background: '#0078d4', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Setup Azure
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(0,120,212,0.06) 0%, transparent 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(0,120,212,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Server size={22} color="#0078d4" />
                  </div>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{acc.label || acc.subscription_id}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{acc.subscription_id}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => void handleSync(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    <RefreshCw size={13} /> Sync
                  </button>
                  <button onClick={() => void handleDisconnect(acc.id)} style={{ padding: '8px', color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', padding: '24px' }}>
                {[
                  { label: 'AI Services', count: acc.ai_services, icon: <Zap size={18} />, color: '#0078d4' },
                  { label: 'Storage', count: acc.storage_accounts, icon: <Database size={18} />, color: '#10b981' },
                  { label: 'Functions', count: acc.functions_count, icon: <HardDrive size={18} />, color: '#6366f1' },
                  { label: 'Groups', count: acc.resource_groups, icon: <Cloud size={18} />, color: '#f59e0b' },
                ].map(r => (
                  <div key={r.label} style={{ padding: '16px', background: 'var(--body-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ marginBottom: '8px', color: r.color }}>{r.icon}</div>
                    <div style={{ fontSize: '24px', fontWeight: 800 }}>{r.count}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
